import { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import Button from '../ui/Button';
import { jobService } from '../../services/jobService';
import { notificationService } from '../../services/notificationService';
import { settingsService } from '../../services/settingsService';
import { userJobPreferencesService } from '../../services/userJobPreferencesService';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';

// Tab components
import BasicInfoTab from './tabs/BasicInfoTab';
import SchedulePaymentsTab from './tabs/SchedulePaymentsTab';
import RequirementsTab from './tabs/RequirementsTab';
import AssignmentTab from './tabs/AssignmentTab';

// Initial form state - Updated for Clients + Job Sites restructure
const INITIAL_FORM_DATA = {
  // Basic Info
  jobName: '',
  description: '',
  priority: 'medium',
  // Client + Job Site (replaces serviceReceiver, partnerAgency, isInternal, jobSource)
  client: null,
  clientId: null,
  jobSite: null,
  jobSiteId: null,
  // Address from Job Site (view-only, stored as snapshot)
  originalAddress: '',
  locationLat: null,
  locationLng: null,
  // Schedule
  jobType: 'one_off',
  scheduledDate: '',
  scheduledTime: '',
  recurringSchedule: null,
  recurringEndDate: null,
  estimatedHours: null,
  // Payment
  showPaymentSection: true, // User preference toggle
  clientChargeType: 'hourly',
  clientChargeAmount: null,
  clientIncludeGst: true,
  clientGstType: 'exclusive', // 'inclusive' | 'exclusive'
  paymentType: 'hourly',
  paymentAmount: null,
  contractorIncludeGst: false,
  contractorGstType: 'exclusive', // 'inclusive' | 'exclusive'
  // Requirements
  photosRequiredBefore: true,
  photosRequiredAfter: true,
  clockInRadius: null,
  clockInFlexibility: false,
  questionnaireTemplate: [],
  safetyRequirements: '',
  equipmentList: '',
  // Assignment (all jobs are private now - isPublished removed)
  assignedContractor: null,
  // Contact Person - auto-filled from Job Site with override option
  useJobSiteContact: true, // Whether to use job site's contact
  contactPersonName: '',
  contactPersonPhone: '',
  contactPersonEmail: '',
  otherDetails: ''
};

export default function CreateJobModal({
  isOpen,
  onClose,
  onJobCreated,
  jobToEdit = null,
  initialTab = null, // 'basic' | 'schedule' | 'requirements' | 'assignment'
  focusContractorSearch = false // Auto-focus the contractor search input when opening assignment tab
}) {
  const { currentCompany } = useCompany();
  const { user } = useAuth();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [gstRate, setGstRate] = useState(10); // Default 10% GST
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Fetch company settings for GST rate
  useEffect(() => {
    const fetchGstRate = async () => {
      if (!currentCompany?.id) return;
      try {
        const settings = await settingsService.getCompanySettings(currentCompany.id);
        if (settings?.default_tax_rate) {
          setGstRate(settings.default_tax_rate);
        }
      } catch (err) {
        console.warn('Could not fetch GST rate, using default:', err);
      }
    };
    fetchGstRate();
  }, [currentCompany?.id]);

  // Fetch user job preferences (for payment section visibility)
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!currentCompany?.id || !user?.id || preferencesLoaded) return;
      try {
        const prefs = await userJobPreferencesService.getPreferences(user.id, currentCompany.id);
        setFormData(prev => ({
          ...prev,
          showPaymentSection: prefs?.showPaymentSection ?? true
        }));
        setPreferencesLoaded(true);
      } catch (err) {
        console.warn('Could not fetch user preferences, using defaults:', err);
        setPreferencesLoaded(true);
      }
    };
    fetchPreferences();
  }, [currentCompany?.id, user?.id, preferencesLoaded]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (jobToEdit) {
        // Populate form for editing
        console.log('Editing Job, Instance Data:', {
          date: jobToEdit.scheduledDate,
          time: jobToEdit.scheduledTime,
          contractor: jobToEdit.assignedContractor
        });

        // Determine if using job site contact
        const jobSiteContact = jobToEdit.jobSite;
        const useJobSiteContact = jobSiteContact?.contactPerson &&
          jobToEdit.contactPersonName === jobSiteContact.contactPerson;

        setFormData({
          ...INITIAL_FORM_DATA,
          ...jobToEdit,
          // Map new fields
          client: jobToEdit.client || null,
          clientId: jobToEdit.clientId || null,
          jobSite: jobToEdit.jobSite || null,
          jobSiteId: jobToEdit.jobSiteId || null,
          originalAddress: jobToEdit.originalAddress || jobToEdit.locationAddress || '',
          // Ensure nulls are handled for controlled inputs
          description: jobToEdit.description || '',
          otherDetails: jobToEdit.otherDetails || '',
          contactPersonName: jobToEdit.contactPersonName || '',
          contactPersonPhone: jobToEdit.contactPersonPhone || '',
          contactPersonEmail: jobToEdit.contactPersonEmail || '',
          safetyRequirements: jobToEdit.safetyRequirements || '',
          equipmentList: jobToEdit.equipmentList || '',
          useJobSiteContact,
          // Ensure correct types
          locationLat: jobToEdit.locationLat || null,
          locationLng: jobToEdit.locationLng || null,
          // Instance data from service update
          scheduledDate: jobToEdit.scheduledDate || '',
          scheduledTime: jobToEdit.scheduledTime || '',
          assignedContractor: jobToEdit.assignedContractor || null
        });
      } else {
        // Reset for new create - preserve showPaymentSection from preferences
        setFormData(prev => ({
          ...INITIAL_FORM_DATA,
          showPaymentSection: prev.showPaymentSection
        }));
      }
      setErrors({});
      setActiveTab(initialTab || 'basic');
      setSubmitError('');
      setPreferencesLoaded(false); // Re-fetch preferences on next open
    }
  }, [isOpen, jobToEdit, initialTab]);

  // Handle form field changes
  const handleChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear related errors
    const errorKeys = Object.keys(updates);
    setErrors(prev => {
      const newErrors = { ...prev };
      errorKeys.forEach(key => {
        delete newErrors[key];
      });
      return newErrors;
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Basic Info validation
    if (!formData?.jobName?.trim()) {
      newErrors.jobName = 'Job name is required';
    } else if (formData.jobName.trim().length < 3) {
      newErrors.jobName = 'Job name must be at least 3 characters';
    }

    // Client is required
    if (!formData?.clientId) {
      newErrors.client = 'Client is required';
    }

    // Job Site is required
    if (!formData?.jobSiteId) {
      newErrors.jobSite = 'Job site is required';
    }

    // Address validation (should come from job site)
    if (!formData?.originalAddress?.trim()) {
      newErrors.originalAddress = 'Address is required (select a job site with an address)';
    }

    // Schedule validation
    if (formData?.jobType === 'one_off') {
      if (!formData?.scheduledDate) {
        newErrors.scheduledDate = 'Scheduled date is required';
      }
    } else {
      if (!formData?.recurringSchedule?.days?.length) {
        newErrors.recurringSchedule = 'Select at least one day for recurring job';
      }
    }

    // Payment validation (only if payment section is shown)
    if (formData?.showPaymentSection && !formData?.clientChargeAmount) {
      newErrors.clientChargeAmount = 'Client charge amount is required';
    }

    // Contact email validation
    if (formData?.contactPersonEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactPersonEmail)) {
      newErrors.contactPersonEmail = 'Invalid email format';
    }

    // Australian phone validation (basic)
    if (formData?.contactPersonPhone) {
      const cleanPhone = formData.contactPersonPhone.replace(/\s/g, '');
      if (!/^(\+61|0)[0-9]{9,10}$/.test(cleanPhone)) {
        newErrors.contactPersonPhone = 'Invalid Australian phone number';
      }
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      // Switch to tab with first error
      const errorFields = Object.keys(validationErrors);
      if (errorFields.some(f => ['jobName', 'client', 'jobSite'].includes(f))) {
        setActiveTab('basic');
      } else if (errorFields.some(f => ['originalAddress', 'scheduledDate', 'recurringSchedule', 'clientChargeAmount', 'paymentAmount'].includes(f))) {
        setActiveTab('schedule');
      } else if (errorFields.some(f => ['contactPersonPhone', 'contactPersonEmail'].includes(f))) {
        setActiveTab('assignment');
      }

      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Prepare job data with new Client + Job Site structure
      const jobData = {
        jobName: formData?.jobName?.trim(),
        description: formData?.description?.trim(),
        priority: formData?.priority,
        // New Client + Job Site fields
        clientId: formData?.clientId,
        jobSiteId: formData?.jobSiteId,
        originalAddress: formData?.originalAddress?.trim(),
        locationLat: formData?.locationLat,
        locationLng: formData?.locationLng,
        // Schedule
        jobType: formData?.jobType,
        recurringSchedule: formData?.jobType === 'recurring' ? formData?.recurringSchedule : null,
        recurringEndDate: formData?.jobType === 'recurring' ? formData?.recurringEndDate : null,
        estimatedHours: formData?.estimatedHours,
        // Payment (only if section is shown)
        clientChargeType: formData?.showPaymentSection ? formData?.clientChargeType : null,
        clientChargeAmount: formData?.showPaymentSection ? formData?.clientChargeAmount : null,
        paymentType: formData?.paymentType,
        paymentAmount: formData?.paymentAmount,
        // Requirements
        photosRequiredBefore: formData?.photosRequiredBefore,
        photosRequiredAfter: formData?.photosRequiredAfter,
        clockInRadius: formData?.clockInRadius,
        clockInFlexibility: formData?.clockInFlexibility,
        questionnaireTemplate: formData?.questionnaireTemplate?.length > 0 ? formData.questionnaireTemplate : null,
        safetyRequirements: formData?.safetyRequirements?.trim() || null,
        equipmentList: formData?.equipmentList?.trim() || null,
        // Contact Person
        contactPersonName: formData?.contactPersonName?.trim() || null,
        contactPersonPhone: formData?.contactPersonPhone?.trim() || null,
        contactPersonEmail: formData?.contactPersonEmail?.trim() || null,
        otherDetails: formData?.otherDetails?.trim() || null,
        // Status based on assignment (all jobs are private now)
        status: formData?.assignedContractor ? 'active' : 'draft'
      };

      // Create or Update
      let resultJob;
      if (jobToEdit) {
        // Update Job
        resultJob = await jobService.updateJob(jobToEdit.id, jobData);

        // Handle Instance Updates (Schedule / Contractor Assignment)
        const prevContractorId = jobToEdit.assignedContractor?.userId || jobToEdit.assignedContractor?.id;
        const newContractorId = formData.assignedContractor?.userId || formData.assignedContractor?.id;
        const isNewAssignment = newContractorId && newContractorId !== prevContractorId;

        if (jobToEdit.latestInstanceId && formData.jobType === 'one_off') {
          // Status should be 'assigned' if a contractor is assigned, 'unassigned' otherwise
          const instanceStatus = newContractorId ? 'assigned' : 'unassigned';
          await jobService.updateJobInstanceStatus(jobToEdit.latestInstanceId,
            instanceStatus,
            {
              scheduled_date: formData.scheduledDate,
              scheduled_time: formData.scheduledTime,
              assigned_contractor: newContractorId || null
            }
          );

          // Notify if new assignment
          if (isNewAssignment) {
            console.log('Notifying contractor:', newContractorId, 'for instance:', jobToEdit.latestInstanceId);
            await notificationService.notifyJobAssigned(
              newContractorId,
              currentCompany?.id,
              { id: jobToEdit.latestInstanceId },
              formData.jobName
            );
          }
        } else if (formData.jobType === 'one_off' && !jobToEdit.latestInstanceId) {
          // Edge case: Job existed but no instance? Create one.
          const newInstance = await jobService.createJobInstance(
            jobToEdit.id,
            currentCompany?.id,
            formData.scheduledDate,
            formData.scheduledTime || null,
            newContractorId
          );

          if (isNewAssignment) {
            await notificationService.notifyJobAssigned(
              newContractorId,
              currentCompany?.id,
              newInstance,
              formData.jobName
            );
          }
        }

      } else {
        // Create New Job
        const createdJob = await jobService.createJob(jobData, currentCompany?.id);
        resultJob = createdJob;

        // Save payment section preference if user toggled it off
        if (!formData.showPaymentSection && user?.id) {
          try {
            await userJobPreferencesService.updatePreferences(user.id, currentCompany.id, {
              showPaymentSection: false
            });
          } catch (err) {
            console.warn('Failed to save payment preference:', err);
          }
        }

        // If contractor is assigned, create job instance (Create Only)
        let createdInstance = null;
        if (formData?.assignedContractor && formData?.jobType === 'one_off') {
          createdInstance = await jobService.createJobInstance(
            createdJob?.id,
            currentCompany?.id,
            formData?.scheduledDate,
            formData?.scheduledTime || null,
            formData?.assignedContractor?.userId || formData?.assignedContractor?.id
          );

          // Send notification to contractor
          await notificationService.notifyJobAssigned(
            formData?.assignedContractor?.userId || formData?.assignedContractor?.id,
            currentCompany?.id,
            createdInstance,
            formData?.jobName
          );
        } else if (formData?.jobType === 'recurring' && formData?.recurringSchedule) {
          // Create recurring instances based on user-selected count
          const instanceCount = formData?.initialInstanceCount || 5;
          const createdInstances = await jobService.createRecurringInstances(
            createdJob?.id,
            currentCompany?.id,
            createdJob?.job_display_id,
            formData?.recurringSchedule,
            instanceCount
          );

          // If contractor selected, assign to FIRST instance only
          if (formData?.assignedContractor && createdInstances?.length > 0) {
            const contractorId = formData?.assignedContractor?.userId || formData?.assignedContractor?.id;
            await jobService.assignContractor(createdInstances[0]?.id, contractorId);

            // Send notification for the first instance only
            await notificationService.notifyJobAssigned(
              contractorId,
              currentCompany?.id,
              createdInstances[0],
              formData?.jobName
            );
          }
        } else if (formData?.jobType === 'one_off' && !formData?.assignedContractor) {
          // Create unassigned instance for one-time job
          await jobService.createJobInstance(
            createdJob?.id,
            currentCompany?.id,
            formData?.scheduledDate,
            formData?.scheduledTime || null
          );
        }
      }

      // Success - close modal and notify parent
      onJobCreated?.(resultJob);
      onClose?.();

    } catch (err) {
      console.error('Failed to create job:', err);
      setSubmitError(err?.message || 'Failed to create job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed top-8 left-60 right-0 bottom-0 bg-black/90 flex items-center justify-center p-6 z-50">
      <div className="bg-background rounded-lg w-full max-w-3xl h-[calc(100vh-8rem)] max-h-[700px] flex flex-col border border-border shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <h2 className="text-xl font-bold text-text-primary">{jobToEdit ? 'Edit Job' : 'Create New Job'}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Banner */}
        {submitError && (
          <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{submitError}</p>
          </div>
        )}

        {/* Tabs Content - New 4-Tab Structure */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden min-h-0">
            <TabsList className="px-6 flex-shrink-0 bg-background">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="schedule">Schedule & Payments</TabsTrigger>
              <TabsTrigger value="requirements">Requirements</TabsTrigger>
              <TabsTrigger value="assignment">Assignment</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto px-6 pb-4 bg-background">
              <TabsContent value="basic">
                <BasicInfoTab
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  companyId={currentCompany?.id}
                  jobDisplayId={jobToEdit?.jobDisplayId}
                />
              </TabsContent>

              <TabsContent value="schedule">
                <SchedulePaymentsTab
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  gstRate={gstRate}
                  userId={user?.id}
                  companyId={currentCompany?.id}
                />
              </TabsContent>

              <TabsContent value="requirements">
                <RequirementsTab
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              </TabsContent>

              <TabsContent value="assignment">
                <AssignmentTab
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  companyId={currentCompany?.id}
                  autoFocusContractorSearch={focusContractorSearch && activeTab === 'assignment'}
                />
              </TabsContent>
            </div>
          </Tabs>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-background flex-shrink-0">
            <div className="text-xs text-white-shade-2">
              {formData?.assignedContractor && (
                <span className="text-primary-orange">
                  Contractor will be notified on creation
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {jobToEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  jobToEdit ? 'Update Job' : 'Create Job'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
